<template lang="pug">
  v-container.sale-info-bar(
    fluid
    :class="{ 'is-vertical': isMobile }"
  )
    .sale-info-bar__column(v-if="!isMobile && !noSaleStarted")
      .sale-info-bar__title Connected To
      .sale-info-bar__value {{ connectedWallet }}
    .sale-info-bar__column
      .sale-info-bar__title Phase
      .sale-info-bar__value {{ saleStatus }}
    transition(name="fade")
      .sale-info-bar__column(v-if="noSaleStarted || isPreSaleLive || (isPublicSaleLive && isUserConnected && contractData.hasPopulated && !hasDAReachedFloor)")
        .countdown
          .sale-info-bar__title {{ timeTitle }}
          countdown(
            :key="countdownInterval"
            :time="countdownEpoch"
            :emit-events="true"
            @end="timeIsUp"
          )
            template(slot-scope="props")
              .sale-info-bar__value
                | {{ countdownTime(props) }}

</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';

import saleMixin from '@/mixins/sale-mixins';

export default {
  name: 'SaleInfoBar',
  metaInfo() {
    return {
      title: this.pageTitle,
    };
  },
  mixins: [saleMixin],
  data() {
    return {
      countdownInterval: 1,
      isNewDAInterval: false,
      isfirstLoad: false,
    };
  },
  props: {
    contractData: { type: Object, default: () => {} },
    isMobile: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters('accounts', ['getActiveAccountRedacted', 'isUserConnected', 'getAccountData']),
    ...mapGetters('contracts', ['getIOContract', 'getDAData']),
    pageTitle() {
      return `${this.hasPreSaleEnded ? 'Dutch Auction' : 'Whitelist'} Mint${
        this.noSaleStarted ? ' Coming Soon' : ''
      }`;
    },
    connectedWallet() {
      return this.isUserConnected ? this.getActiveAccountRedacted : 'Wallet Not Connected';
    },
    timeTitle() {
      if (this.noSaleStarted) {
        return `Time To ${
          this.hasPreSaleEnded ? (this.isMobile ? 'DA' : 'Dutch Auction') : 'Whitelist'
        } Mint`;
      } else if (this.isPublicSaleLive) {
        return 'Next Price Drop';
      }
      return 'Time Left To Mint';
    },
    saleStatus() {
      if (process.env.VUE_APP_FORCE_END === 'true') {
        return 'End of Mint';
      } else if (!this.contractData.hasPopulated) {
        // If presale epoch has passed show public sale epoch
        if (this.noSaleStarted) {
          return 'Counting Down';
        } else {
          return this.isPublicSaleLive
            ? `Public ${this.isMobile ? 'DA' : 'Dutch Auction'} Mint`
            : 'Private Whitelist Mint';
        }
      } else if (this.isPublicSaleLive) {
        return `Public ${this.isMobile ? 'DA' : 'Dutch Auction'} Mint`;
      }
      return 'Private Whitelist Mint';
    },
    dropInterval() {
      return this.contractData.publicSaleDropInterval * 1000;
    },
    countdownEpoch() {
      if (!this.contractData.hasPopulated) {
        if (this.noSaleStarted) {
          const saleDateTime = new Date(
            this.hasPreSaleEnded ? this.publicSaleEpoch : this.preSaleEpoch
          );

          return saleDateTime - Date.now();
        }

        return this.preSaleEndDate - Date.now();
      } else if (this.isPublicSaleLive) {
        // Get the interval for the next price drop
        if (this.isNewDAInterval) {
          this.resetInterval();
          return this.dropInterval;
        } else {
          let currentIntervalDecimal =
            1 - (this.countdownInterval - Math.floor(this.countdownInterval));
          if (isNaN(currentIntervalDecimal)) {
            currentIntervalDecimal = 0;
          }
          return currentIntervalDecimal * this.dropInterval;
        }
      }

      return this.preSaleEndDate - Date.now();
    },
    hasDAReachedFloor() {
      return this.isPublicSaleLive && this.getDAData.price === this.contractData.floorPrice
        ? true
        : false;
    },
  },
  methods: {
    ...mapMutations('contracts', ['setIsNewDAInterval']),
    ...mapActions('contracts', ['storeDAData']),
    padDigits(num, size) {
      return num.toLocaleString('en-US', { minimumIntegerDigits: size, useGrouping: false });
    },
    countdownTime(props) {
      if (this.isPublicSaleLive) {
        return `${this.padDigits(props.minutes, 2)} : ${this.padDigits(props.seconds, 2)}`;
      }
      return `${
        props.days > 0 ? props.days + (parseInt(props.days) > 1 ? ' days, ' : ' day, ') : ''
      } ${this.padDigits(props.hours, 2)} : ${this.padDigits(props.minutes, 2)} : ${this.padDigits(
        props.seconds,
        2
      )}`;
    },
    async updateCountdownInterval() {
      let willExceed =
        ((this.getDAData.currentTime * 1000 - this.publicSaleEpoch - 15000) / this.dropInterval) %
          1 >
        0.85;
      this.countdownInterval =
        (this.getDAData.currentTime * 1000 - this.publicSaleEpoch - (willExceed ? 0 : 15000)) /
        this.dropInterval;
    },
    async timeIsUp() {
      if (this.isPublicSaleLive) {
        // Public sale ongoing, reset the interval
        this.resetPublisSaleTimer();
      } else if (!this.isPreSaleLive || Date.now() >= this.preSaleEndDate) {
        // Show button to refresh page
        this.$router.go();
      }
    },
    resetInterval() {
      this.isNewDAInterval = false;
    },
    resetPublisSaleTimer() {
      this.isNewDAInterval = true;
      this.countdownInterval++;
    },
  },
  watch: {
    getAccountData: {
      async handler() {
        if (this.getAccountData.hasPopulated) {
          await this.storeDAData();
          this.updateCountdownInterval();
        }
      },
      immediate: true,
    },
    'getDAData.price': function (price) {
      if (!this.isfirstLoad) {
        this.isfirstLoad = true;
        return;
      }

      this.resetPublisSaleTimer();
    },
  },
};
</script>

<style lang="scss">
.sale-info-bar {
  position: absolute;
  bottom: 0;
  z-index: 3;
  height: 80px;
  background: $light-beige;
  display: flex;
  align-items: center;
  justify-content: space-around;

  &.is-vertical {
    justify-content: space-between;
  }
}

.sale-info-bar__title {
  color: $dark-grey-1;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
}

.sale-info-bar__value {
  color: $black;
  font-weight: 700;
  text-transform: uppercase;
}

.v-btn--mint-now {
  position: absolute;

  @include mobile {
    height: 40px !important;
    right: 20px;
  }
}
</style>
